// src/components/skills/SkillDetailModal.tsx
//
// Modal de detalhe da skill (Reforma UX §6.7, SKL-02..05, wireframe dlgSkill).
// Abas: SKILL.md · References · Evals · Execuções · Telemetria.
// Botões: Disparar · Nova versão · Excluir (+ Fechar).
//
// - References: clique abre leitor fullscreen (SkillReferenceReader) e volta.
// - Execuções: realtime (skill_executions na publication supabase_realtime),
//   clique abre SkillExecutionDetailModal (input · linha do tempo · output).
// - Telemetria: scoring 6D real (relevancia/negocios/conteudo/operacao/
//   novidade/credibilidade 0-100 + weighted_mean), sizing, duração média.
//   GAPS do backend (GET /v1/skills/:slug NÃO retorna): p95_duration_ms (existe
//   na coluna mas fica fora do payload) e custo médio (derivamos das execuções
//   carregadas via cost_usd — rotulado; sem execuções vira "—").
// - SKILL.md: READ-ONLY. Não existe PATCH /v1/skills no backend (o "Editar" da
//   página antiga chamava endpoint inexistente) e o conteúdo é MD-only no storage.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type SkillDetail,
  type SkillExecution,
} from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, type TabItem } from '@/components/ui/Tabs';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { SkillExecuteModal } from '@/components/SkillExecuteModal';
import { SkillExecutionDetailModal } from '@/components/SkillExecutionDetailModal';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { SkillReferenceReader } from './SkillReferenceReader';
import { SkillNewVersionModal } from './SkillNewVersionModal';
import {
  SCOPE_BADGE,
  MATURITY_BADGE,
  SCORING_6D_LABELS,
  SCORING_6D_META_KEYS,
  formatDurationMs,
  normalizeReferences,
  normalizeEvals,
  parseStepsCount,
  type NormalizedReference,
} from './skill-constants';
import { fmtMoneyUSD, fmtNumber } from '@/lib/format';
import {
  FileText,
  BookOpen,
  FlaskConical,
  Activity,
  BarChart3,
  Play,
  GitBranch,
  Trash2,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Copy,
} from 'lucide-react';

type Tab = 'skill-md' | 'references' | 'evals' | 'executions' | 'telemetry';

const TAB_ITEMS: TabItem[] = [
  { value: 'skill-md', label: 'SKILL.md', icon: FileText },
  { value: 'references', label: 'Referências', icon: BookOpen },
  { value: 'evals', label: 'Evals', icon: FlaskConical },
  { value: 'executions', label: 'Execuções', icon: Activity },
  { value: 'telemetry', label: 'Telemetria', icon: BarChart3 },
];

const EXEC_STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  running: 'info',
  success: 'success',
  ok: 'success',
  error: 'danger',
  failed: 'danger',
  cancelled: 'default',
};

/** Execução com campos extras que o backend retorna mas o type não declara (api.ts intocável). */
type ExecutionRow = SkillExecution & { cost_usd?: number | null; trigger_summary?: string | null };

export interface SkillDetailModalProps {
  /** id OU slug — GET /v1/skills/:slug resolve ambos. */
  skillId: string;
  open: boolean;
  onClose: () => void;
  /** Chamado após DELETE confirmado — a página remove da lista e fecha. */
  onDeleted: (slug: string) => void;
  onToast?: (msg: string) => void;
}

export function SkillDetailModal({ skillId, open, onClose, onDeleted, onToast }: SkillDetailModalProps) {
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [executions, setExecutions] = useState<ExecutionRow[]>([]);
  const [executionsError, setExecutionsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('skill-md');

  // Sub-modais
  const [executeOpen, setExecuteOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [openExecutionId, setOpenExecutionId] = useState<string | null>(null);
  const [readerRef, setReaderRef] = useState<NormalizedReference | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const flash = useCallback(
    (msg: string) => {
      setActionMsg(msg);
      setTimeout(() => setActionMsg(null), 6000);
      onToast?.(msg);
    },
    [onToast],
  );

  const refetchExecutions = useCallback(() => {
    if (!skillId) return;
    api
      .listSkillExecutions(skillId)
      .then((exs) => {
        setExecutions(exs as ExecutionRow[]);
        setExecutionsError('');
      })
      .catch((err: unknown) => {
        setExecutionsError(err instanceof Error ? err.message : 'Erro ao carregar execuções');
      });
  }, [skillId]);

  useEffect(() => {
    if (!skillId || !open) return;
    let cancelled = false;
    // Reset assíncrono (microtask) — evita setState síncrono dentro do effect
    // (react-hooks/set-state-in-effect).
    Promise.resolve().then(() => {
      if (cancelled) return;
      setLoading(true);
      setError('');
      setActiveTab('skill-md');
    });

    Promise.all([
      api.getSkill(skillId),
      api.listSkillExecutions(skillId).catch((err: unknown) => {
        if (!cancelled) {
          setExecutionsError(err instanceof Error ? err.message : 'Erro ao carregar execuções');
        }
        return [] as SkillExecution[];
      }),
    ])
      .then(([s, exs]) => {
        if (cancelled) return;
        setSkill(s);
        setExecutions(exs as ExecutionRow[]);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar skill');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [skillId, open]);

  // SKL-02/AC: execuções em realtime — skill_executions está na publication
  // supabase_realtime (migration 20260428040000). Filtra pelo uuid da skill.
  useSupabaseRealtime(
    { table: 'skill_executions', filter: skill?.id ? `skill_id=eq.${skill.id}` : undefined },
    refetchExecutions,
  );

  const references = useMemo(() => normalizeReferences(skill?.references_bundle), [skill]);
  const evals = useMemo(() => normalizeEvals(skill?.evals_contract), [skill]);
  const stepsCount = useMemo(() => parseStepsCount(skill?.skill_md_content), [skill]);

  const scoring = (skill?.scoring_6d ?? null) as Record<string, unknown> | null;
  const scoringDims = useMemo(() => {
    if (!scoring) return [] as Array<[string, number]>;
    return Object.entries(scoring).filter(
      (e): e is [string, number] => typeof e[1] === 'number' && !SCORING_6D_META_KEYS.has(e[0]),
    );
  }, [scoring]);
  const weightedMean = typeof scoring?.['weighted_mean'] === 'number' ? (scoring['weighted_mean'] as number) : null;
  const credibility = typeof scoring?.['credibilidade'] === 'number' ? (scoring['credibilidade'] as number) : null;

  // % sucesso + custo médio derivados das execuções REAIS carregadas (rotulado).
  const finished = useMemo(
    () => executions.filter((e) => e.status === 'success' || e.status === 'error'),
    [executions],
  );
  const successRate = finished.length > 0
    ? Math.round((finished.filter((e) => e.status === 'success').length / finished.length) * 100)
    : null;
  const avgCost = useMemo(() => {
    const costs = executions
      .map((e) => e.cost_usd)
      .filter((c): c is number => typeof c === 'number');
    if (costs.length === 0) return null;
    return costs.reduce((a, b) => a + b, 0) / costs.length;
  }, [executions]);

  const handleEvaluate = async () => {
    if (!skill) return;
    flash('Disparando evals...');
    try {
      const r = await api.evaluateSkill(skill.slug);
      flash(`Evals concluídas: ${r.passed}/${r.total_cases} passaram, ${r.failed} falharam.`);
    } catch (err) {
      flash(`Erro nos evals: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async () => {
    if (!skill) return;
    await api.deleteSkill(skill.slug);
    onDeleted(skill.slug);
  };

  const copySlug = () => {
    if (!skill) return;
    void navigator.clipboard?.writeText(skill.slug);
    flash('Slug copiado.');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent
          size="xl"
          className="max-h-[90vh] flex flex-col p-0 overflow-hidden"
          // SKL-A: com o leitor de referência aberto, interação/Esc fecham SÓ o
          // leitor — sem isso o Radix tratava o leitor como "fora" do dialog e
          // fechava a modal por baixo (voltava pra lista com refetch geral).
          onInteractOutside={(e) => {
            if (readerRef) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (readerRef) {
              e.preventDefault();
              setReaderRef(null);
            }
          }}
        >
          {/* Header */}
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-raios-border shrink-0">
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-5 h-5 text-raios-accent-text" aria-hidden />
              <span>{skill?.name ?? 'Skill'}</span>
              {skill && (
                <>
                  <span className="text-xs font-mono font-normal text-raios-muted">{skill.slug}</span>
                  <button
                    type="button"
                    onClick={copySlug}
                    title="Copiar slug"
                    aria-label="Copiar slug"
                    className="p-1 rounded hover:bg-raios-elevated text-raios-muted"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <Badge variant="info" className="font-mono text-[10px]">v{skill.version}</Badge>
                  <Badge variant={SCOPE_BADGE[skill.scope] ?? 'default'} className="text-[10px]">{skill.scope}</Badge>
                  {skill.maturity_level && (
                    <Badge variant={MATURITY_BADGE[skill.maturity_level] ?? 'default'} className="text-[10px]">
                      {skill.maturity_level}
                    </Badge>
                  )}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-64 rounded-lg" />
                <Skeleton className="h-48 rounded-xl" />
              </div>
            ) : error || !skill ? (
              <EmptyState
                icon={AlertCircle}
                title="Erro ao carregar skill"
                description={error || 'Skill não encontrada'}
              />
            ) : (
              <>
                <Tabs
                  items={TAB_ITEMS}
                  value={activeTab}
                  onChange={(v) => setActiveTab(v as Tab)}
                  className="mb-4"
                />

                {/* ── SKILL.md ── */}
                {activeTab === 'skill-md' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <MetaCell label="Slug" value={skill.slug} mono />
                      <MetaCell label="Versão" value={`v${skill.version}`} mono />
                      <MetaCell label="Scope" value={skill.scope} />
                      <MetaCell label="Steps" value={stepsCount != null ? String(stepsCount) : '—'} />
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary bg-raios-elevated/30 border border-raios-border rounded-md p-4 overflow-x-auto">
                      {skill.skill_md_content || '(sem conteúdo — SKILL.md não resolvido no storage)'}
                    </pre>
                  </div>
                )}

                {/* ── References ── */}
                {activeTab === 'references' && (
                  <div className="space-y-3">
                    {references.length === 0 ? (
                      <EmptyState
                        icon={BookOpen}
                        title="Nenhuma referência"
                        description="Esta skill não possui referências anexadas."
                      />
                    ) : (
                      <>
                        <p className="text-xs text-raios-muted">
                          Arquivos em <code className="font-mono">references/</code> da skill — clique pra abrir o leitor.
                        </p>
                        <div className="flex flex-col gap-2">
                          {references.map((ref) => (
                            <button
                              key={ref.name}
                              type="button"
                              onClick={() => setReaderRef(ref)}
                              className="flex items-center gap-2 px-3 py-2 rounded-md border border-raios-border bg-raios-elevated/30 hover:bg-raios-elevated text-left text-sm font-mono text-raios-text-secondary transition-colors"
                            >
                              <BookOpen className="w-4 h-4 text-raios-accent-text shrink-0" aria-hidden />
                              {ref.name}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ── Evals ── */}
                {activeTab === 'evals' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-raios-muted">
                        {evals.length > 0
                          ? `${evals.length} caso${evals.length > 1 ? 's' : ''} no evals_contract.`
                          : ''}
                      </p>
                      <Button variant="default" size="sm" onClick={() => void handleEvaluate()}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Rodar evals
                      </Button>
                    </div>
                    {evals.length === 0 ? (
                      <EmptyState
                        icon={FlaskConical}
                        title="Nenhum eval"
                        description="Esta skill não possui casos de eval cadastrados."
                      />
                    ) : (
                      evals.map((ev, i) => <EvalCaseCard key={i} index={i} evalCase={ev} />)
                    )}
                  </div>
                )}

                {/* ── Execuções ── */}
                {activeTab === 'executions' && (
                  <div className="space-y-2">
                    <p className="text-xs text-raios-muted">
                      Execuções desta skill —{' '}
                      <span className="text-raios-success-text font-medium">realtime</span>, mais recente →
                      mais antiga. Clique pra abrir <strong>input · linha do tempo · output</strong>.
                    </p>
                    {executionsError ? (
                      <EmptyState
                        icon={AlertCircle}
                        title="Erro ao carregar execuções"
                        description={executionsError}
                      />
                    ) : executions.length === 0 ? (
                      <EmptyState
                        icon={Activity}
                        title="Nenhuma execução"
                        description="Esta skill ainda não foi executada."
                      />
                    ) : (
                      <div className="divide-y divide-raios-border-muted border border-raios-border rounded-md">
                        {executions.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => setOpenExecutionId(e.id)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-raios-elevated/50 transition-colors text-left"
                          >
                            <Badge variant={EXEC_STATUS_BADGE[e.status] ?? 'default'} className="text-[10px] shrink-0">
                              {e.status}
                            </Badge>
                            <span className="text-xs text-raios-text-secondary flex-1 truncate">
                              {e.trigger_summary ?? e.trigger ?? `execução ${e.id.slice(0, 8)}`}
                            </span>
                            <span className="text-xs tabular-nums text-raios-muted shrink-0">
                              {formatDurationMs(e.duration_ms)}
                            </span>
                            <span className="text-xs text-raios-muted shrink-0">
                              {new Date(e.started_at).toLocaleString('pt-BR')}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Telemetria ── */}
                {activeTab === 'telemetry' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <MetaCell
                        label="Execuções"
                        value={(skill.total_executions ?? 0).toLocaleString('pt-BR')}
                      />
                      <MetaCell
                        label={successRate != null ? `Sucesso (últimas ${finished.length})` : 'Sucesso'}
                        value={successRate != null ? `${successRate}%` : '—'}
                      />
                      <MetaCell label="Duração média" value={formatDurationMs(skill.avg_duration_ms)} />
                      <MetaCell label="Sizing" value={skill.sizing ?? '—'} />
                    </div>

                    {scoringDims.length > 0 ? (
                      <Card className="p-5">
                        <p className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-3">
                          Scoring 6D
                        </p>
                        <div className="space-y-2">
                          {scoringDims.map(([dim, score]) => (
                            <div key={dim} className="flex items-center gap-3">
                              <span className="text-xs text-raios-text-secondary w-28 shrink-0">
                                {SCORING_6D_LABELS[dim] ?? dim}
                              </span>
                              <div className="flex-1 h-2 rounded-full bg-raios-elevated overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-raios-accent"
                                  style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                                />
                              </div>
                              <span className="text-xs tabular-nums font-semibold text-raios-text w-12 text-right">
                                {fmtNumber(score, 2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    ) : (
                      <EmptyState
                        icon={BarChart3}
                        title="Scoring 6D ainda não calculado"
                        description={`Gerado pelo cron diário quando a skill atinge 10+ execuções. Atual: ${(skill.total_executions ?? 0).toLocaleString('pt-BR')} execuções.`}
                      />
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <MetaCell
                        label="Credibilidade"
                        value={credibility != null ? `${fmtNumber(credibility, 2)} / 100` : '—'}
                      />
                      <MetaCell
                        label="Weight admin"
                        value={weightedMean != null ? `${fmtNumber(weightedMean, 2)} / 100` : '—'}
                      />
                      <MetaCell
                        label={avgCost != null ? `Custo médio (últimas ${executions.length})` : 'Custo médio'}
                        value={avgCost != null ? fmtMoneyUSD(avgCost, 2) : '—'}
                      />
                      <MetaCell
                        label="p95"
                        value={formatDurationMs(skill.p95_duration_ms)}
                      />
                    </div>
                    <p className="text-[11px] text-raios-muted leading-relaxed">
                      <strong>Weight admin</strong> = weighted_mean do scoring 6D (média ponderada das
                      dimensões com pesos do admin). <strong>Sizing</strong> é o porte auto-calculado
                      (MICRO · CURTA · MEDIA · LONGA). % sucesso e custo médio são derivados das
                      execuções carregadas; p95 e custo agregado por skill ainda não vêm do
                      <code className="font-mono"> GET /v1/skills/:slug</code>.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer — Fechar · Excluir · Nova versão · Disparar */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-raios-border shrink-0">
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(true)}
              disabled={!skill}
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </Button>
            <Button variant="default" onClick={() => setNewVersionOpen(true)} disabled={!skill}>
              <GitBranch className="w-4 h-4" />
              Nova versão
            </Button>
            <Button variant="brand" onClick={() => setExecuteOpen(true)} disabled={!skill}>
              <Play className="w-4 h-4" />
              Disparar
            </Button>
          </div>

          {/* Toast inline do modal */}
          {actionMsg && (
            <div className="absolute bottom-20 right-6 z-10 max-w-md rounded-lg border border-raios-border bg-raios-surface px-4 py-3 shadow-lg text-sm">
              {actionMsg}
            </div>
          )}

        </DialogContent>
      </Dialog>

      {/* Leitor de referência — TELA CHEIA via portal (wireframe dlgReader).
          A modal por baixo NÃO fecha: os guards onInteractOutside/
          onEscapeKeyDown do DialogContent seguram enquanto readerRef != null,
          e "Fechar"/Esc voltam pra ela (SKL-D). */}
      <SkillReferenceReader
        reference={readerRef}
        open={readerRef !== null}
        onClose={() => setReaderRef(null)}
      />

      {/* Disparar */}
      {executeOpen && skill && (
        <SkillExecuteModal
          open={executeOpen}
          onClose={() => setExecuteOpen(false)}
          skill={{
            slug: skill.slug,
            name: skill.name,
            version: skill.version,
            inputs_schema:
              (skill as unknown as { inputs_schema?: Record<string, unknown> }).inputs_schema ?? {},
          }}
          onDispatched={(taskId) => {
            flash(`Skill disparada — task ${taskId.slice(0, 8)}. Acompanhe na aba Execuções.`);
            setActiveTab('executions');
          }}
        />
      )}

      {/* Nova versão */}
      {newVersionOpen && skill && (
        <SkillNewVersionModal
          open={newVersionOpen}
          onClose={() => setNewVersionOpen(false)}
          skill={{ slug: skill.slug, version: skill.version }}
          onCreated={flash}
        />
      )}

      {/* Excluir (soft-delete + confirmação) */}
      {deleteOpen && skill && (
        <ConfirmActionDialog
          open={deleteOpen}
          onOpenChange={(o) => { if (!o) setDeleteOpen(false); }}
          title="Excluir skill?"
          description="Soft-delete no backend (recuperável por admin) — a skill some da listagem. Audit log captura via trigger."
          confirmLabel="Excluir"
          confirmVariant="destructive"
          confirmIcon={Trash2}
          onConfirm={handleDelete}
        >
          <p className="text-sm text-raios-text-secondary">
            Skill: <span className="font-mono text-raios-text">{skill.slug}</span>
            {skill.name && ` — ${skill.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Detalhe de execução (input · linha do tempo · output) */}
      <SkillExecutionDetailModal
        executionId={openExecutionId}
        open={openExecutionId !== null}
        onClose={() => setOpenExecutionId(null)}
      />
    </>
  );
}

function MetaCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="bg-raios-elevated/30 border border-raios-border rounded-md p-3 min-w-0">
      <p className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-1 truncate" title={label}>
        {label}
      </p>
      <p className={`text-sm font-semibold text-raios-text tabular-nums truncate ${mono ? 'font-mono' : ''}`} title={value}>
        {value}
      </p>
    </div>
  );
}

function EvalCaseCard({ index, evalCase }: { index: number; evalCase: Record<string, unknown> }) {
  const e = evalCase as {
    id?: string;
    name?: string;
    label?: string;
    input?: unknown;
    expected_behavior?: unknown;
    expected_output_contains?: unknown;
  };
  const inputStr =
    typeof e.input === 'string' ? e.input : e.input ? JSON.stringify(e.input, null, 2) : '—';
  const expected =
    typeof e.expected_behavior === 'string'
      ? e.expected_behavior
      : e.expected_behavior
        ? JSON.stringify(e.expected_behavior, null, 2)
        : typeof e.expected_output_contains === 'string'
          ? e.expected_output_contains
          : e.expected_output_contains
            ? JSON.stringify(e.expected_output_contains, null, 2)
            : '—';
  const headLabel = e.label ?? e.name ?? e.id ?? `case-${index + 1}`;
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="info">#{index + 1}</Badge>
        <span className="text-sm font-medium text-raios-text">{String(headLabel)}</span>
      </div>
      <div>
        <p className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-1">Input</p>
        <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary bg-raios-elevated/30 border border-raios-border rounded-md p-3 overflow-x-auto">
          {inputStr}
        </pre>
      </div>
      <div>
        <p className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-1">
          Comportamento esperado
        </p>
        <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary bg-raios-elevated/30 border border-raios-border rounded-md p-3 overflow-x-auto">
          {expected}
        </pre>
      </div>
    </Card>
  );
}
